import { COUNTRIES } from '../countries';
import { esc } from '../esc';
import {
  STATE_REQUIRED_COUNTRIES, fieldError, missingFields, normaliseInput, validate,
  type BillingAddress, type BillingField,
} from '../billing-address';
import { PaymentApiError, getBillingAddress, saveBillingAddress, toPaymentError } from '../payments-api';
import type { BillingAddressView } from '../types';
import { ALERT_ERR_CLS, BTN_CLS, FIELD_ERR_CLS, INPUT_CLS, LABEL_CLS } from './classes';

export interface BillingAddressFormOptions {
  /** Pre-loaded view; when omitted the widget fetches GET /payments/billing-address. */
  initial?: BillingAddressView;
  onSaved?: (view: BillingAddressView) => void;
  onError?: (err: PaymentApiError) => void;
}

export interface BillingAddressFormHandle {
  destroy(): void;
  reload(): Promise<void>;
  getView(): BillingAddressView | null;
}

const TEXT_FIELDS: Array<{ name: BillingField; label: string; autocomplete: string }> = [
  { name: 'firstName', label: 'First name', autocomplete: 'given-name' },
  { name: 'lastName', label: 'Last name', autocomplete: 'family-name' },
  { name: 'line1', label: 'Address line 1', autocomplete: 'address-line1' },
  { name: 'line2', label: 'Address line 2 (optional)', autocomplete: 'address-line2' },
  { name: 'city', label: 'City', autocomplete: 'address-level2' },
  { name: 'zip', label: 'Postal code', autocomplete: 'postal-code' },
];

const REQUIRED_MSG = 'Required';

export function mountBillingAddressForm(el: HTMLElement, opts: BillingAddressFormOptions = {}): BillingAddressFormHandle {
  const textField = (f: (typeof TEXT_FIELDS)[number]) => `
    <label class="block">
      <span class="${LABEL_CLS} mb-1.5">${esc(f.label)}</span>
      <input type="text" name="${f.name}" autocomplete="${f.autocomplete}" class="${INPUT_CLS}" />
      <span data-error="${f.name}" class="${FIELD_ERR_CLS}" hidden></span>
    </label>`;

  el.innerHTML = `
    <form id="billing-address" class="w-full flex flex-col gap-3.5" novalidate>
      <p data-role="status" class="text-sm text-text-dim" aria-live="polite"></p>
      <div data-role="error" class="${ALERT_ERR_CLS}" role="alert" hidden></div>
      <label class="block">
        <span class="${LABEL_CLS} mb-1.5">Country</span>
        <select name="country" autocomplete="country" class="${INPUT_CLS}">
          <option value="">Select country…</option>
          ${COUNTRIES.map((c) => `<option value="${esc(c.code)}">${esc(c.name)}</option>`).join('')}
        </select>
        <span data-error="country" class="${FIELD_ERR_CLS}" hidden></span>
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        ${textField(TEXT_FIELDS[0])}${textField(TEXT_FIELDS[1])}
      </div>
      ${textField(TEXT_FIELDS[2])}${textField(TEXT_FIELDS[3])}
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        ${textField(TEXT_FIELDS[4])}${textField(TEXT_FIELDS[5])}
      </div>
      <label class="block" data-role="state-wrap" hidden>
        <span class="${LABEL_CLS} mb-1.5">State / province code</span>
        <input type="text" name="state" maxlength="3" autocomplete="address-level1" class="${INPUT_CLS} uppercase" />
        <span data-error="state" class="${FIELD_ERR_CLS}" hidden></span>
      </label>
      <button type="submit" class="${BTN_CLS}" data-role="save">Save address</button>
    </form>`;

  const form = el.querySelector('form') as HTMLFormElement;
  const statusEl = el.querySelector('[data-role="status"]') as HTMLElement;
  const errEl = el.querySelector('[data-role="error"]') as HTMLElement;
  const stateWrap = el.querySelector('[data-role="state-wrap"]') as HTMLElement;
  const saveBtn = el.querySelector('[data-role="save"]') as HTMLButtonElement;
  const input = (name: string) => form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement;

  let view: BillingAddressView | null = null;
  let destroyed = false;

  const setFieldError = (field: string, msg: string | null | undefined) => {
    const span = el.querySelector(`[data-error="${field}"]`) as HTMLElement | null;
    if (!span) return;
    span.textContent = msg ?? '';
    span.hidden = !msg;
    input(field)?.setAttribute('aria-invalid', msg ? 'true' : 'false');
  };
  const clearErrors = () => {
    ['country', ...TEXT_FIELDS.map((f) => f.name), 'state'].forEach((f) => setFieldError(f, null));
    errEl.hidden = true;
  };
  const showStateIfNeeded = () => {
    stateWrap.hidden = !STATE_REQUIRED_COUNTRIES.includes(input('country').value);
  };
  const renderStatus = () => {
    if (!view) { statusEl.textContent = ''; return; }
    statusEl.textContent = view.complete
      ? 'Billing address complete.'
      : `Billing address incomplete${view.missing.length ? ` (missing: ${view.missing.join(', ')})` : ''}.`;
    statusEl.className = `text-sm ${view.complete ? 'text-success' : 'text-text-dim'}`;
  };
  const fill = (v: BillingAddressView) => {
    view = v;
    for (const key of Object.keys(v.address) as BillingField[]) {
      const inp = input(key);
      if (inp) inp.value = v.address[key] ?? '';
    }
    showStateIfNeeded();
    renderStatus();
  };

  const read = (): BillingAddress => {
    const raw: Partial<Record<BillingField, unknown>> = {};
    for (const f of [...TEXT_FIELDS.map((t) => t.name), 'country', 'state'] as BillingField[]) raw[f] = input(f).value;
    if (!STATE_REQUIRED_COUNTRIES.includes(String(raw.country ?? '').toUpperCase())) raw.state = '';
    const n = normaliseInput(raw);
    return {
      firstName: null, lastName: null, line1: null, line2: null, city: null, zip: null, state: null, country: null,
      ...n,
    };
  };

  const onCountry = () => { showStateIfNeeded(); };
  const onBlur = (e: Event) => {
    const t = e.target as HTMLInputElement;
    if (!t.name || t.tagName === 'SELECT') return;
    const addr = read();
    setFieldError(t.name, fieldError(t.name as BillingField, addr[t.name as BillingField], addr.country));
  };

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    clearErrors();
    const addr = read();
    const errors: Record<string, string> = { ...validate(addr) };
    // Only country/names/address are hard-required on save; missing ones are flagged too.
    for (const f of missingFields(addr)) if (!errors[f]) errors[f] = REQUIRED_MSG;
    if (Object.keys(errors).length) {
      for (const [f, m] of Object.entries(errors)) setFieldError(f, m);
      return;
    }
    saveBtn.disabled = true;
    try {
      const saved = await saveBillingAddress(addr as unknown as Partial<BillingAddress>);
      if (destroyed) return;
      fill(saved);
      opts.onSaved?.(saved);
    } catch (err) {
      const pe = toPaymentError(err);
      if (destroyed) return;
      const fe = pe.fieldErrors;
      if (Object.keys(fe).length) {
        for (const [f, m] of Object.entries(fe)) setFieldError(f, m);
      } else {
        errEl.textContent = pe.message;
        errEl.hidden = false;
      }
      opts.onError?.(pe);
    } finally {
      saveBtn.disabled = false;
    }
  };

  const reload = async () => {
    try {
      const v = await getBillingAddress();
      if (!destroyed) fill(v);
    } catch (err) {
      if (destroyed) return;
      const pe = toPaymentError(err);
      errEl.textContent = pe.message;
      errEl.hidden = false;
      opts.onError?.(pe);
    }
  };

  input('country').addEventListener('change', onCountry);
  form.addEventListener('focusout', onBlur);
  form.addEventListener('submit', onSubmit);

  if (opts.initial) fill(opts.initial);
  else void reload();

  return {
    destroy() {
      destroyed = true;
      input('country').removeEventListener('change', onCountry);
      form.removeEventListener('focusout', onBlur);
      form.removeEventListener('submit', onSubmit);
      el.innerHTML = '';
    },
    reload,
    getView: () => view,
  };
}
